/**
 * A deliberately small Markdown renderer.
 *
 * It covers what a guidebook page uses: headings, paragraphs, lists, bold,
 * italic, links, images, blockquotes and horizontal rules. It escapes every
 * piece of text before it goes near the output, and it does not support raw
 * HTML, which is the whole point: content is edited as files in this repo and
 * rendered into a shell that a guest opens on their own phone.
 *
 * It is here rather than as a dependency because the alternative was a
 * markdown library, a sanitizer, and the two of them staying in step. If a
 * page ever needs tables or footnotes, that is the moment to add `marked` and
 * `dompurify` and say why.
 */

export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function inline(text: string): string {
  let out = escapeHtml(text);

  // Images first, so the link rule does not eat them.
  out = out.replace(/!\[([^\]]*)\]\(([^)\s]+)\)/g, (_m, alt: string, src: string) =>
    isSafeUrl(src) ? `<img src="${src}" alt="${alt}" loading="lazy">` : alt,
  );

  out = out.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_m, label: string, href: string) => {
    if (!isSafeUrl(href)) return label;
    const external = /^https?:/i.test(href);
    return external
      ? `<a href="${href}" target="_blank" rel="noopener noreferrer">${label}</a>`
      : `<a href="${href}">${label}</a>`;
  });

  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  out = out.replace(/(^|[^*])\*([^*]+)\*/g, '$1<em>$2</em>');
  out = out.replace(/`([^`]+)`/g, '<code>$1</code>');

  return out;
}

/** No javascript:, no data:, no vbscript:. Relative and http(s) and tel and mailto only. */
function isSafeUrl(url: string): boolean {
  return /^(https?:|mailto:|tel:|\/|#|\.)/i.test(url);
}

export function renderMarkdown(markdown: string): string {
  const out: string[] = [];
  let list: 'ul' | 'ol' | null = null;
  let paragraph: string[] = [];

  const closeParagraph = () => {
    if (paragraph.length) {
      out.push(`<p>${inline(paragraph.join(' '))}</p>`);
      paragraph = [];
    }
  };
  const closeList = () => {
    if (list) {
      out.push(`</${list}>`);
      list = null;
    }
  };

  for (const rawLine of markdown.split(/\r?\n/)) {
    const line = rawLine.trimEnd();

    if (!line.trim()) {
      closeParagraph();
      closeList();
      continue;
    }

    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    if (heading) {
      closeParagraph();
      closeList();
      const level = heading[1]!.length;
      out.push(`<h${level}>${inline(heading[2] ?? '')}</h${level}>`);
      continue;
    }

    if (/^(-{3,}|\*{3,})$/.test(line.trim())) {
      closeParagraph();
      closeList();
      out.push('<hr>');
      continue;
    }

    const quote = /^>\s?(.*)$/.exec(line);
    if (quote) {
      closeParagraph();
      closeList();
      out.push(`<blockquote>${inline(quote[1] ?? '')}</blockquote>`);
      continue;
    }

    const bullet = /^[-*]\s+(.*)$/.exec(line.trim());
    const numbered = /^\d+[.)]\s+(.*)$/.exec(line.trim());
    if (bullet || numbered) {
      closeParagraph();
      const wanted = bullet ? 'ul' : 'ol';
      if (list !== wanted) {
        closeList();
        out.push(`<${wanted}>`);
        list = wanted;
      }
      out.push(`<li>${inline((bullet ?? numbered)![1] ?? '')}</li>`);
      continue;
    }

    closeList();
    paragraph.push(line.trim());
  }

  closeParagraph();
  closeList();
  return out.join('\n');
}
